const GOOGLE_SCRIPT_ID = 'google-identity-services';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
            ux_mode?: 'popup' | 'redirect';
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            context?: 'signin' | 'signup' | 'use';
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, string | number | boolean>
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export const getGoogleClientId = () =>
  import.meta.env.VITE_GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID_PROD || '';

let googleScriptPromise: Promise<void> | null = null;

export const loadGoogleIdentityScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google 登录仅支持在浏览器中使用'));
  }
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (googleScriptPromise) {
    return googleScriptPromise;
  }

  googleScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google 登录脚本加载失败')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google 登录脚本加载失败'));
    document.head.appendChild(script);
  });

  return googleScriptPromise;
};

interface RenderGoogleButtonOptions {
  container: HTMLElement;
  text: 'signin_with' | 'signup_with';
  onCredential: (credential: string) => void | Promise<void>;
  onError?: (error: Error) => void;
}

export const renderGoogleButton = async ({
  container,
  text,
  onCredential,
  onError
}: RenderGoogleButtonOptions) => {
  const clientId = getGoogleClientId();
  if (!clientId) {
    throw new Error('缺少 VITE_GOOGLE_CLIENT_ID，无法启用 Google 登录');
  }

  await loadGoogleIdentityScript();
  if (!window.google?.accounts?.id) {
    throw new Error('Google 登录 SDK 初始化失败');
  }

  container.innerHTML = '';
  window.google.accounts.id.initialize({
    client_id: clientId,
    ux_mode: 'popup',
    context: text === 'signup_with' ? 'signup' : 'signin',
    cancel_on_tap_outside: true,
    callback: ({ credential }) => {
      if (!credential) {
        onError?.(new Error('未获取到 Google 凭证'));
        return;
      }
      Promise.resolve(onCredential(credential)).catch((error) => {
        onError?.(error instanceof Error ? error : new Error('Google 登录处理失败'));
      });
    }
  });

  window.google.accounts.id.renderButton(container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text,
    shape: 'rectangular',
    width: Math.max(container.clientWidth, 320),
    logo_alignment: 'left'
  });
};
